import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchMoleculeComponent } from 'src/app/components/input-switch-molecule/input-switch-molecule.component';
import { EditorModule } from 'primeng/editor';

import {
  FormFieldMoleculeComponent,
  LabelAtomComponent,
} from '@ls-commons/components';

import { HttpClient } from '@angular/common/http';
import { CheckboxModule } from 'primeng/checkbox';
import { ModalService } from '@ls-commons/utils/modals';
import { CalendarModule } from 'primeng/calendar';
import { FieldsetModule } from 'primeng/fieldset';
import { DividerModule } from 'primeng/divider';
import jsPDF from 'jspdf';
// import { format } from 'date-fns/format';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { NgIf } from '@angular/common';
import {
  symptomsValidator,
  divingWithValidator,
  divingTypeValidator,
} from './checkboxes-required.validator';

@Component({
  selector: 'app-sinister-form',
  standalone: true,
  imports: [
    FormFieldMoleculeComponent,
    ButtonModule,
    SelectButtonModule,
    LabelAtomComponent,
    ReactiveFormsModule,
    SkeletonModule,
    CheckboxModule,
    InputSwitchMoleculeComponent,
    DropdownModule,
    EditorModule,
    CalendarModule,
    FieldsetModule,
    DividerModule,
    ToggleButtonModule,
    NgIf,
  ],
  templateUrl: './sinister-form.component.html',
  styleUrl: './sinister-form.component.scss',
})
export class SinisterFormComponent {
  sinisterForm!: FormGroup;

  modalService = inject(ModalService);
  destroyRef = inject(DestroyRef);
  http = inject(HttpClient);

  defaultDate = new Date();

  private _isLoading = signal<boolean>(true);
  public isLoading = computed(() => this._isLoading());

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    readonly fb: FormBuilder,
    private el: ElementRef
  ) {
    this.sinisterForm = this.fb.group(
      {
        name: ['', Validators.required],
        age: ['', Validators.required], //
        accident_date: ['', Validators.required],
        telephone: ['', Validators.required], //
        email: ['', [Validators.required, Validators.email]], //
        nif: ['', Validators.required], //
        diving_center_name: ['', Validators.required],
        diver_certification: ['', Validators.required],
        open_water_course: [''],

        ear_pain: [''],
        headache: [''],
        falls: [''],
        seasickness: [''],
        joint_pain: [''],
        skin_spots: [''],
        nausea: [''],
        limbs_numbness: [''],
        extreme_fatigue: [''],
        vertigo: [''],
        limbs_tingling: [''],
        semi_drowning: [''],

        max_depth: ['', Validators.required],
        total_immersion_time: ['', Validators.required],
        stop_decompression_omission: [''],
        quick_ascent: [''],
        panic: [''],

        // diving_with: [null, Validators.required],
        diving_air: [''],
        diving_nitrox: [''],
        diving_trimix: [''],
        diving_rebreather: [''],

        // diving_type: [null, Validators.required],
        diving_type_first_dive: [''],
        diving_type_successive: [''],

        equipment_failure: [''],
        course_attending: [''],
        course_attending_name: [''],

        medical_report: [''],
        bills: [''],
        bank_account_certificate: [''],
      },
      {
        validators: [
          symptomsValidator(),
          divingWithValidator(),
          divingTypeValidator(),
        ],
      }
    );
  }

  closeDialog() {
    this.ref.close(false);
  }

  sendForm() {
    this.sinisterForm.markAllAsTouched();

    if (this.sinisterForm.valid) {
      const page = document.querySelector('.page') as HTMLElement;
      const sinisterFormData = this.sinisterForm.getRawValue();
      const { name, accident_date, email } = sinisterFormData;

      // const filename = `${name} ${format(
      //   accident_date || new Date(),
      //   'Y-m-d'
      // )}.pdf`;
      const doc = new jsPDF();
      doc.setProperties({
        // title: filename,
        title: $localize`:@@forms.sinister.title:Nueva declaración de siniestro`,
      });

      doc.html(page, {
        callback: doc => {
          // doc.save(filename);

          const pdfBlob = doc.output('blob');
          const reader = new FileReader();
          reader.readAsArrayBuffer(pdfBlob);
          reader.onloadend = () => {
            const pdfArrayBuffer = reader.result as ArrayBuffer;
            const url = `pdf/generate-send-pdf?name=${name}&accident_date=${accident_date}&email=${email}`;
            this.http
              .post(url, pdfArrayBuffer, {
                responseType: 'text',
                headers: { 'Content-Type': 'application/pdf' },
              })
              .subscribe({
                next: () => {
                  this.modalService.showSuccessMessage(
                    `Su declaración de siniestro ha sido enviada correctamente.`
                  );

                  this.ref.close(true);
                },
                error: () => {
                  this.modalService.showErrorMessage(
                    `El proceso no se ha podido completar.`
                  );
                },
              });
          };
        },
        margin: 5,
        // x: 10,
        y: 5,
        width: 200,
        windowWidth: 900,
      });
    } else {
      this.modalService.showErrorMessage(
        `Es necesario rellenar todos los campos obligatorios`
      );
    }

    if (this.sinisterForm.valid) {
      // Lógica para enviar el formulario
      console.log('Formulario enviado', this.sinisterForm.value);
    } else {
      console.log('Formulario no válido');
    }
  }

  // async sinisterFormToPDF() {
  //   this.sinisterForm.markAllAsTouched();

  //   if (this.sinisterForm.valid) {
  //     const page = document.querySelector('.page') as HTMLElement;
  //     const sinisterFormData = this.sinisterForm.getRawValue();
  //     const { name, accident_date } = sinisterFormData;

  //     const filename = `${name} ${format(
  //       accident_date || new Date(),
  //       'Y-m-d'
  //     )}.pdf`;
  //     const doc = new jsPDF();
  //     doc.setProperties({
  //       title: filename,
  //     });

  //     doc.html(page, {
  //       callback: doc => {
  //         doc.save(filename);

  //         this.modalService.showSuccessMessage(
  //           `Formulario enviado correctamente`
  //         );

  //       },
  //       margin: 5,
  //       // x: 10,
  //       y: 5,
  //       width: 200,
  //       windowWidth: 900,
  //     });
  //   } else {
  //     this.modalService.showErrorMessage(
  //       `Es necesario rellenar todos los campos obligatorios`
  //     );
  //   }
  // }
}
